import React, { useState, useEffect, useRef } from 'react';
import { useCRM } from '../context/CRMContext';

// ─── ACTION META ──────────────────────────────────────────────────────────────
const ACTION_META = {
  lead_created:       { label: 'Lead Created',       color: '#10b981', bg: 'rgba(16,185,129,0.12)',  icon: '➕' },
  lead_updated:       { label: 'Lead Updated',       color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  icon: '✏️' },
  stage_change:       { label: 'Stage Changed',      color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)',  icon: '🔀' },
  note_added:         { label: 'Note Added',         color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',   icon: '📝' },
  followup_added:     { label: 'Follow-up Added',    color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  icon: '📅' },
  followup_completed: { label: 'Follow-up Done',     color: '#10b981', bg: 'rgba(16,185,129,0.12)',  icon: '✅' },
  assessment_sent:    { label: 'Assessment Sent',    color: '#ec4899', bg: 'rgba(236,72,153,0.12)',  icon: '📋' },
  lead_deleted:       { label: 'Lead Deleted',       color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   icon: '🗑️' },
  login:              { label: 'Logged In',          color: '#0ea5e9', bg: 'rgba(14,165,233,0.12)',  icon: '🔑' },
  leave_logged_by_admin: { label: 'Leave Logged (Admin)', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: '🗓️' },
};

function formatTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatDateGroup(ts) {
  if (!ts) return 'Unknown date';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function getDateKey(ts) {
  if (!ts) return 'unknown';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toDateString();
}

// ─── DETAIL RENDERER ─────────────────────────────────────────────────────────
function ActionDetail({ action, details, stages }) {
  if (!details) return null;
  if (action === 'stage_change') {
    const getStageLabel = (id) => {
      const s = stages.find(s => s.id === id || s.name === id);
      return s ? s.name : id;
    };
    return (
      <span style={{ fontSize: 12, color: '#475569' }}>
        <span style={{ color: '#ef4444', fontWeight: 500 }}>{getStageLabel(details.oldStage)}</span>
        {' → '}
        <span style={{ color: '#10b981', fontWeight: 500 }}>{getStageLabel(details.newStage)}</span>
      </span>
    );
  }
  if (action === 'lead_created') {
    return (
      <span style={{ fontSize: 12, color: '#475569' }}>
        {details.source && <span>Source: <b>{details.source}</b> · </span>}
        {details.assignedTo && <span>Assigned to: <b>{details.assignedTo}</b></span>}
      </span>
    );
  }
  if (action === 'lead_updated' && details.fields) {
    return <span style={{ fontSize: 12, color: '#475569' }}>Updated: <b>{details.fields.join(', ')}</b></span>;
  }
  if (action === 'note_added' && details.preview) {
    return <span style={{ fontSize: 12, color: '#475569', fontStyle: 'italic' }}>"{details.preview}{details.preview.length >= 120 ? '...' : ''}"</span>;
  }
  if (action === 'followup_added') {
    return (
      <span style={{ fontSize: 12, color: '#475569' }}>
        {details.type && <span>Type: <b>{details.type}</b></span>}
        {details.dueDate && <span> · Due: <b>{details.dueDate}</b></span>}
        {details.note && <span> · "{details.note.slice(0, 60)}"</span>}
      </span>
    );
  }
  if (action === 'assessment_sent' && details.title) {
    return <span style={{ fontSize: 12, color: '#475569' }}>Title: <b>{details.title}</b></span>;
  }
  if (action === 'login' && details.loginAt) {
    return <span style={{ fontSize: 12, color: '#475569' }}>{formatTime(details.loginAt)}</span>;
  }
  if (action === 'leave_logged_by_admin' && details.type) {
    return (
      <span style={{ fontSize: 12, color: '#475569' }}>
        {details.employeeName && <span>For: <b>{details.employeeName}</b> · </span>}
        Type: <b>{details.type}</b>
        {details.from && <span> · {details.from}{details.to && details.to !== details.from ? ` → ${details.to}` : ''}</span>}
        {typeof details.days === 'number' && <span> · {details.days} day{details.days === 1 ? '' : 's'}</span>}
      </span>
    );
  }
  return null;
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function ActivityLog() {
  const { users, pipelineStages, currentUser, rrSettings, setCurrentPage, subscribeActivityLogs } = useCRM();

  const [logs,           setLogs]           = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filterUser,     setFilterUser]     = useState('all');
  const [filterAction,   setFilterAction]   = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo,   setFilterDateTo]   = useState('');
  const [search,         setSearch]         = useState('');
  const unsubRef = useRef(null);

  // Visibility check — admins always see; counselors only if in logVisibility list
  const logVisibility = rrSettings?.logVisibility || [];
  const canView = currentUser.role === 'admin' || logVisibility.includes(currentUser.id);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    if (unsubRef.current) unsubRef.current();
    // Always use the simple orderBy-only query — no composite index needed.
    // Date filtering is applied client-side in the 'filtered' memo below.
    unsubRef.current = subscribeActivityLogs(
      (data) => { setLogs(data); setLoading(false); },
      (err) => { console.error('ActivityLog subscription error:', err); setLoading(false); }
    );
    return () => { if (unsubRef.current) unsubRef.current(); };
  }, [canView]);

  if (!canView) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Access Restricted</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>You don't have permission to view the Activity Log.</div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>Ask your admin to grant access in Settings → Round Robin → Activity Log Visibility.</div>
      </div>
    );
  }

  // Filter logs
  const filtered = logs.filter(log => {
    if (filterUser !== 'all' && log.userId !== filterUser) return false;
    if (filterAction !== 'all' && log.action !== filterAction) return false;
    if (filterDateTo) {
      const to = new Date(filterDateTo); to.setHours(23, 59, 59);
      const ts = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
      if (ts > to) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!(log.leadName || '').toLowerCase().includes(q) &&
          !(log.userName || '').toLowerCase().includes(q) &&
          !(log.action || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Group by date
  const groups = [];
  const seen = {};
  filtered.forEach(log => {
    const key = getDateKey(log.timestamp);
    if (!seen[key]) {
      seen[key] = true;
      groups.push({ key, label: formatDateGroup(log.timestamp), logs: [] });
    }
    groups[groups.length - 1].logs.push(log);
  });
  // Fix: reassign correctly (since we push logs at the end, but logs for different days can mix)
  const groupMap = {};
  filtered.forEach(log => {
    const key = getDateKey(log.timestamp);
    if (!groupMap[key]) groupMap[key] = { key, label: formatDateGroup(log.timestamp), logs: [] };
    groupMap[key].logs.push(log);
  });
  const sortedGroups = Object.values(groupMap).sort((a, b) => {
    const da = a.logs[0]?.timestamp?.toDate ? a.logs[0].timestamp.toDate() : new Date(a.logs[0]?.timestamp);
    const db2 = b.logs[0]?.timestamp?.toDate ? b.logs[0].timestamp.toDate() : new Date(b.logs[0]?.timestamp);
    return db2 - da;
  });

  const totalToday = (groupMap[new Date().toDateString()]?.logs || []).length;

  return (
    <div className="page-enter" style={{ height: '100%', overflowY: 'auto', padding: '24px 28px', maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>Activity Log</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            Complete audit trail of all counsellor actions
            {currentUser.role !== 'admin' && <span style={{ color: '#f59e0b', marginLeft: 8, fontSize: 11, fontWeight: 600 }}>● Read-only view</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {totalToday > 0 && (
            <div style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 20, padding: '4px 12px', fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>
              {totalToday} actions today
            </div>
          )}
          {currentUser.role === 'admin' && (
            <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setCurrentPage('settings')}>
              ⚙️ Configure Visibility
            </button>
          )}
        </div>
      </div>

      {/* Filters row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="crm-input"
          placeholder="Search lead, counsellor, action..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 200px', minWidth: 200 }}
        />
        <select
          className="crm-input"
          value={filterUser}
          onChange={e => setFilterUser(e.target.value)}
          style={{ flex: '0 0 170px' }}
        >
          <option value="all">All Counsellors</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name || u.email}</option>
          ))}
        </select>
        <select
          className="crm-input"
          value={filterAction}
          onChange={e => setFilterAction(e.target.value)}
          style={{ flex: '0 0 170px' }}
        >
          <option value="all">All Actions</option>
          {Object.entries(ACTION_META).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
        <input
          type="date"
          className="crm-input"
          value={filterDateFrom}
          onChange={e => setFilterDateFrom(e.target.value)}
          title="From date"
          style={{ flex: '0 0 140px' }}
        />
        <input
          type="date"
          className="crm-input"
          value={filterDateTo}
          onChange={e => setFilterDateTo(e.target.value)}
          title="To date"
          style={{ flex: '0 0 140px' }}
        />
        {(filterUser !== 'all' || filterAction !== 'all' || filterDateFrom || filterDateTo || search) && (
          <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => {
            setFilterUser('all'); setFilterAction('all'); setFilterDateFrom(''); setFilterDateTo(''); setSearch('');
          }}>Clear filters</button>
        )}
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {Object.entries(ACTION_META).map(([k, v]) => {
          const count = filtered.filter(l => l.action === k).length;
          if (!count) return null;
          return (
            <div key={k} onClick={() => setFilterAction(filterAction === k ? 'all' : k)}
              style={{ cursor: 'pointer', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: filterAction === k ? v.bg : 'rgba(0,0,0,0.04)',
                color: filterAction === k ? v.color : '#64748b',
                border: '1px solid ' + (filterAction === k ? v.color + '40' : 'rgba(0,0,0,0.07)') }}>
              {v.icon} {v.label} <span style={{ opacity: 0.7 }}>({count})</span>
            </div>
          );
        })}
      </div>

      {/* Timeline */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
          Loading activity logs...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b' }}>No activity found</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Actions taken by counsellors will appear here in real-time.</div>
        </div>
      ) : (
        sortedGroups.map(group => (
          <div key={group.key} style={{ marginBottom: 32 }}>
            {/* Date header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', whiteSpace: 'nowrap' }}>{group.label}</div>
              <div style={{ flex: 1, height: 1, background: 'rgba(124,58,237,0.12)' }} />
              <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{group.logs.length} action{group.logs.length !== 1 ? 's' : ''}</div>
            </div>

            {/* Log entries */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {group.logs.map((log, idx) => {
                const meta = ACTION_META[log.action] || { label: log.action, color: '#64748b', bg: 'rgba(0,0,0,0.05)', icon: '•' };
                const avatar = log.userAvatar || { bg: '#7c3aed', color: '#fff' };
                const initials = (log.userName || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <div key={log.id || idx}
                    style={{ display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 10, alignItems: 'flex-start',
                      background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.05)',
                      transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.95)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.6)'}
                  >
                    {/* Time */}
                    <div style={{ width: 54, flexShrink: 0, fontSize: 11, color: '#94a3b8', paddingTop: 2, textAlign: 'right' }}>
                      {formatTime(log.timestamp)}
                    </div>

                    {/* Avatar */}
                    <div title={log.userName}
                      style={{ width: 30, height: 30, borderRadius: '50%', background: avatar.bg, color: avatar.color,
                        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700 }}>
                      {initials}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {/* Action chip */}
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                          background: meta.bg, color: meta.color }}>
                          {meta.icon} {meta.label}
                        </span>
                        {/* Counselor name */}
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{log.userName}</span>
                        {log.userRole && log.userRole !== 'admin' && (
                          <span style={{ fontSize: 10, color: '#7c3aed', background: 'rgba(124,58,237,0.08)',
                            padding: '1px 6px', borderRadius: 10 }}>{log.userRole}</span>
                        )}
                      </div>

                      {/* Lead name */}
                      {log.leadName && (
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>Student:</span>
                          <button
                            onClick={() => { if (log.leadId) setCurrentPage('leads'); }}
                            style={{ fontSize: 13, fontWeight: 600, color: '#3b82f6', background: 'none', border: 'none',
                              cursor: 'pointer', padding: 0, textDecoration: 'underline', textDecorationColor: 'transparent',
                              transition: 'text-decoration-color 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.textDecorationColor = '#3b82f6'}
                            onMouseLeave={e => e.currentTarget.style.textDecorationColor = 'transparent'}
                          >
                            {log.leadName}
                          </button>
                        </div>
                      )}

                      {/* Details */}
                      {log.details && (
                        <div style={{ marginTop: 4 }}>
                          <ActionDetail action={log.action} details={log.details} stages={pipelineStages} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {filtered.length > 0 && (
        <div style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', padding: '20px 0' }}>
          Showing {filtered.length} of {logs.length} log entries
        </div>
      )}
    </div>
  );
}
